import {
  FormControl,
  InputLabel,
  OutlinedInput,
  useMediaQuery,
} from "@mui/material";
import "./Permissions.scss";
import { AdminPermissions } from "../../SourceData";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import BlueButton from "../../components/BlueButton/BlueButton";
import Loader from "../../components/Loader";
import api from "../../utils/api";

const Permissions = () => {
  const { id } = useParams(); // Get the role ID from URL params
  const navigate = useNavigate();
  const isEditMode = !!id; // Check if ID exists to determine edit mode

  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [updatedAdmin, setUpdatedAdmin] = useState([]);
  const [checkedPermissions, setCheckedPermissions] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch existing role data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchRoleData();
    }
  }, [id]);

  // Update section IDs whenever checked permissions change
  useEffect(() => {
    updateSelectedSections();
  }, [checkedPermissions]);

  // Function to update selected section IDs based on checked permissions
  const updateSelectedSections = () => {
    const sectionIds = [];

    // We no longer need to add section IDs separately here because our handleCheckboxChange
    // function is now automatically adding the section's view permission (which has the same ID
    // as the section itself) whenever any sub-permission is checked.

    // This function is now just kept for clarity and potential future use.
    // If we need to add any additional section IDs in the future, we can do it here.

    setSelectedSectionIds(sectionIds);
  };

  const fetchRoleData = async () => {
    setFetchLoading(true);
    try {
      const response = await api.get(`/roles/${id}`);

      // Extract role data from the nested response
      const { role } = response.data.data;

      setRoleName(role.name);
      setCheckedPermissions(role.permissions || []);
      setUpdatedAdmin(role);

      // We could also store the full permissions data if needed
      // but for now we're just using the IDs from the role.permissions array
    } catch (err) {
      toast.error(
        err.response?.data?.message || "حدث خطأ أثناء جلب بيانات الصلاحية"
      );
      navigate("/permissions"); // Redirect if role not found
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCheckboxChange = (permissionId, isChecked) => {
    if (isChecked) {
      // Find the section this permission belongs to
      const section = AdminPermissions.find((section) =>
        section.sub_permissions.some((subPerm) => subPerm.id === permissionId)
      );

      // If we found the section and this isn't the first sub-permission (which has the section's ID)
      if (section && permissionId !== section.id) {
        // Check if the first sub-permission (section view permission) is already checked
        const viewPermissionChecked = checkedPermissions.includes(section.id);

        if (!viewPermissionChecked) {
          // If not checked, add both the selected permission and the view permission
          setCheckedPermissions((prevCheckedPermissions) => [
            ...prevCheckedPermissions,
            permissionId,
            section.id, // Add the section ID (view permission)
          ]);
          return;
        }
      }

      // Just add the permission ID in normal cases
      setCheckedPermissions((prevCheckedPermissions) => [
        ...prevCheckedPermissions,
        permissionId,
      ]);
    } else {
      // For unchecking, we need to ensure we don't remove the view permission
      // if other sub-permissions from the same section are still checked

      const section = AdminPermissions.find((section) =>
        section.sub_permissions.some((subPerm) => subPerm.id === permissionId)
      );

      // If this is the section's view permission (first sub-permission with same ID as section)
      if (section && permissionId === section.id) {
        // If we're unchecking the view permission, we should uncheck all sub-permissions in this section
        const otherPermissionIds = section.sub_permissions
          .map((subPerm) => subPerm.id)
          .filter((id) => id !== section.id);

        // Remove the view permission and all other permissions in this section
        setCheckedPermissions((prevCheckedPermissions) =>
          prevCheckedPermissions.filter(
            (id) => !otherPermissionIds.includes(id) && id !== section.id
          )
        );
        return;
      }

      // If this is a regular sub-permission, check if there are any other sub-permissions
      // from the same section still checked (excluding the view permission itself)
      if (section) {
        const otherSubPermIds = section.sub_permissions
          .filter(
            (subPerm) =>
              subPerm.id !== section.id && subPerm.id !== permissionId
          )
          .map((subPerm) => subPerm.id);

        const hasOtherCheckedSubPerms = otherSubPermIds.some((id) =>
          checkedPermissions.includes(id)
        );

        if (!hasOtherCheckedSubPerms) {
          // If no other sub-permissions are checked, remove both this permission and the view permission
          setCheckedPermissions((prevCheckedPermissions) =>
            prevCheckedPermissions.filter(
              (id) => id !== permissionId && id !== section.id
            )
          );
          return;
        }
      }

      // Normal case: just remove this permission
      setCheckedPermissions((prevCheckedPermissions) =>
        prevCheckedPermissions.filter((id) => id !== permissionId)
      );
    }
  };

  const handleRoleNameChange = (e) => {
    setRoleName(e.target.value);
  };

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      setError("الرجاء إدخال اسم الصلاحية");
      return;
    }

    if (checkedPermissions.length === 0) {
      setError("الرجاء اختيار صلاحية واحدة على الأقل");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Combine the sub-permissions with their section IDs
      const allPermissions = [...checkedPermissions, ...selectedSectionIds];

      // Remove duplicates (in case section IDs were already included in checkedPermissions)
      const uniquePermissions = [...new Set(allPermissions)];

      // Prepare the data payload
      const roleData = {
        name: roleName,
        permissions: uniquePermissions,
        // If there's a status field in the original data, preserve it in edit mode
        ...(isEditMode &&
          updatedAdmin.status !== undefined && { status: updatedAdmin.status }),
      };

      // Make the API call - POST for create, PUT for update
      if (isEditMode) {
        await api.put(`/roles/${id}`, roleData);
        toast.success("تم تحديث الصلاحية بنجاح");
        navigate("/admins");
      } else {
        await api.post("/roles", roleData);
        toast.success("تم انشاء الصلاحية بنجاح");
        navigate("/admins");
        // Reset form in create mode
        setRoleName("");
        setCheckedPermissions([]);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEditMode
            ? "حدث خطأ أثناء تحديث الصلاحية"
            : "حدث خطأ أثناء إنشاء الصلاحية")
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine the size class based on the section name or number of permissions
  const getBoxSizeClass = (section) => {
    const permCount = section.sub_permissions?.length || 0;
    const sectionName = section.name;

    // Based on your screenshot layout:
    // First section (لوحة التحكم) is full width
    if (sectionName === "لوحة التحكم") {
      return "top-row";
    }

    // Medium width boxes (like المسؤولين الفرعيين, الصلاحيات, المستخدمين)
    if (
      ["المسؤولين الفرعيين", "الصلاحيات", "المستخدمين", "المتاجر"].includes(
        sectionName
      )
    ) {
      return "medium-width";
    }

    // Alternatively, decide based on number of permissions
    if (permCount > 8) {
      return "top-row";
    } else if (permCount >= 2) {
      return "medium-width";
    }
    // Default to small width
    // return "small-width";
  };

  if (fetchLoading) {
    return <Loader />;
  }

  return (
    <div className="permissions">
      <div className="permissions-header">
        <p className="title">
          {isEditMode ? "تعديل الصلاحية" : "اضافة صلاحية جديدة"}
        </p>
        <FormControl
          sx={{ width: isMobile ? "100%" : isTablet ? "50%" : "900px" }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم الصلاحية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            notched
            value={roleName}
            onChange={handleRoleNameChange}
          />
        </FormControl>
        {error && <p className="error-message">{error}</p>}
      </div>

      <div className="check-group">
        {AdminPermissions?.map((item) => {
          const sizeClass = getBoxSizeClass(item);

          return (
            <div className={`check ${sizeClass}`} key={item.id}>
              <div className="header">{item?.name}</div>
              <div className="body">
                {item?.sub_permissions?.map((i) => (
                  <div className="item" key={i.id}>
                    <input
                      type="checkbox"
                      id={`permission-${i.id}`}
                      checked={checkedPermissions.includes(i.id)}
                      onChange={(e) =>
                        handleCheckboxChange(i.id, e.target.checked)
                      }
                    />
                    <label htmlFor={`permission-${i.id}`}>{i?.name}</label>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="button-container">
        <BlueButton
          text={isEditMode ? "تحديث" : "انشاء"}
          size="large"
          variant="fill"
          type="button"
          loading={loading}
          disable={loading}
          onClick={handleSubmit}
        />
        {isEditMode && (
          <BlueButton
            text="إلغاء"
            size="large"
            variant="outline"
            type="button"
            onClick={() => navigate("/admins")}
            style={{ marginRight: "16px" }}
          />
        )}
      </div>
    </div>
  );
};

export default Permissions;
